import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import { PDFDocument } from "pdf-lib";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import Button from "../../../componentes/button/button";
import Input from "../../../componentes/input/input";
import Textarea from "../../../componentes/input/textarea";
import { CustomDropdown } from "../../../componentes/dropdown/CustomDropdown";
import MeuUsuarioService from "../../../services/resource/meuUsuarioService";
import TipoPagamentoService from "../../../services/resource/tipoPagamentoService";
import RateioOrcamentarioTabela from "../Shared/RateioOrcamentarioTabela";

const SEM_NOTA_FISCAL = "Sem nota fiscal";
const chaveNota = item => item.numeroNotaFiscal || SEM_NOTA_FISCAL;

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
const CMAP_URL = `${process.env.PUBLIC_URL}/pdfjs/cmaps/`;
const STANDARD_FONT_DATA_URL = `${process.env.PUBLIC_URL}/pdfjs/standard_fonts/`;

const meuUsuarioService = new MeuUsuarioService();
const tipoPagamentoService = new TipoPagamentoService();

const LARGURA_MAXIMA_PAGINA = 700;
const MARGEM_PADRAO = 20;
const ESPACO_ENTRE_CARIMBOS = 12;
const LARGURA_PADRAO_ASSINATURA = 160;
const TAMANHO_MINIMO_ASSINATURA = 40;
const TAMANHO_MINIMO_RATEIO = 100;
const TAMANHO_MINIMO_PAGAMENTO = 100;
const TAMANHO_MINIMO_DATA = 60;
const TAMANHO_MINIMO_TEXTO = 100;

// --- Tabela de Rateio Orçamentário desenhada como IMAGEM (igual à assinatura) direto na página do
// documento — não cria mais páginas extras no PDF. ---
const ESCALA_RASTER_TABELA = 2; // renderiza em 2x pra ficar nítida mesmo depois de escalada pra caber
const LARGURA_PADRAO_TABELA_RATEIO = 280; // largura inicial (compacta) — o usuário pode redimensionar depois
const MARGEM_INTERNA_TABELA = 4;
const PADDING_HORIZONTAL_TABELA = 4;
const ALTURA_TITULO_NOTA_TABELA = 18;
const ALTURA_CABECALHO_TABELA = 18;
const ALTURA_LINHA_TABELA = 16;
const ALTURA_LINHA_TOTAL_TABELA = 18;
const ESPACO_ENTRE_NOTAS_TABELA = 10;

function formatarMoedaPdf(valor) {
    return `R$ ${Number(valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Agrupa as linhas de rateio por número da nota fiscal, preservando a ordem de primeira ocorrência. */
function agruparRateioPorNota(itens) {
    const grupos = [];
    const porNota = new Map();
    itens.forEach(item => {
        const numero = chaveNota(item);
        let grupo = porNota.get(numero);
        if (!grupo) {
            grupo = { numeroNotaFiscal: numero, itens: [], total: 0 };
            porNota.set(numero, grupo);
            grupos.push(grupo);
        }
        grupo.itens.push(item);
        grupo.total += Number(item.valor) || 0;
    });
    return grupos;
}

/**
 * Renderiza a tabela de Rateio Orçamentário (agrupada por Nota Fiscal, com o total de cada nota)
 * como uma imagem PNG, via canvas offscreen — o rateio entra no PDF do mesmo jeito que a
 * assinatura, como uma imagem carimbada na própria página do documento.
 *
 * Retorna `largura`/`altura` no "espaço de desenho" (a mesma unidade dos pixels do <canvas> de
 * exibição da página — 1 dessas unidades vira 1 ponto de PDF ao dividir pela escala da página, o
 * mesmo cálculo já usado pra posicionar a assinatura) — é isso que o restante do componente usa pra
 * posicionar e encolher a imagem até caber no espaço disponível, não a resolução do PNG em si (que
 * é sempre `ESCALA_RASTER_TABELA`x maior, só pra ficar nítida ao encolher).
 */
function gerarImagemTabelaRateio(itens, larguraDesenho) {
    const grupos = agruparRateioPorNota(itens);
    const larguraUtil = larguraDesenho - PADDING_HORIZONTAL_TABELA * 2;
    // Centro de Custo, Conta e Valor têm a largura do maior texto da coluna (medido no próprio canvas),
    // senão códigos longos (ex.: conta com 10 dígitos) invadem a coluna do lado. Filial fica com o resto.
    const medidor = document.createElement("canvas").getContext("2d");
    const medir = (texto, negrito = false) => {
        medidor.font = `${negrito ? "bold " : ""}9px Helvetica, Arial, sans-serif`;
        return medidor.measureText(String(texto ?? "")).width;
    };
    const FOLGA_COLUNA_TABELA = 10;
    const todosItens = grupos.flatMap(grupo => grupo.itens);
    const larguraColuna = (cabecalho, textos) =>
        Math.max(medir(cabecalho, true), ...textos.map(texto => medir(texto))) + FOLGA_COLUNA_TABELA;
    const larguras = {
        centro: larguraColuna("C.Custo", todosItens.map(item => item.centroCusto)),
        conta: larguraColuna("Conta", todosItens.map(item => item.contaLabel)),
        valor: Math.max(
            larguraColuna("Valor", todosItens.map(item => formatarMoedaPdf(item.valor))),
            ...grupos.map(grupo => medir(formatarMoedaPdf(grupo.total), true) + FOLGA_COLUNA_TABELA),
        ),
    };
    larguras.filial = Math.max(larguraUtil - larguras.centro - larguras.conta - larguras.valor, 40);
    // Filial é texto livre: se não couber, corta por largura em pixels (não por nº de caracteres).
    const ajustarFilial = texto => {
        let resultado = String(texto ?? "");
        const limite = larguras.filial - FOLGA_COLUNA_TABELA;
        if (medir(resultado) <= limite) return resultado;
        while (resultado.length > 1 && medir(`${resultado}…`) > limite) resultado = resultado.slice(0, -1);
        return `${resultado}…`;
    };

    let alturaDesenho = MARGEM_INTERNA_TABELA;
    grupos.forEach(grupo => {
        alturaDesenho += ALTURA_TITULO_NOTA_TABELA + ALTURA_CABECALHO_TABELA
            + grupo.itens.length * ALTURA_LINHA_TABELA + ALTURA_LINHA_TOTAL_TABELA + ESPACO_ENTRE_NOTAS_TABELA;
    });
    alturaDesenho += MARGEM_INTERNA_TABELA - ESPACO_ENTRE_NOTAS_TABELA; // sem espaço depois da última nota

    const canvas = document.createElement("canvas");
    canvas.width = larguraDesenho * ESCALA_RASTER_TABELA;
    canvas.height = alturaDesenho * ESCALA_RASTER_TABELA;
    const ctx = canvas.getContext("2d");
    ctx.scale(ESCALA_RASTER_TABELA, ESCALA_RASTER_TABELA);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, larguraDesenho, alturaDesenho);
    ctx.strokeStyle = "#262626";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, larguraDesenho - 1, alturaDesenho - 1);
    ctx.translate(PADDING_HORIZONTAL_TABELA, 0);

    let y = MARGEM_INTERNA_TABELA;

    grupos.forEach(grupo => {
        ctx.font = "bold 11px Helvetica, Arial, sans-serif";
        ctx.fillStyle = "#262626";
        ctx.fillText(`NOTA FISCAL: ${grupo.numeroNotaFiscal}`, 0, y + 12);
        y += ALTURA_TITULO_NOTA_TABELA;

        ctx.fillStyle = "#e5e7eb";
        ctx.fillRect(0, y, larguraUtil, ALTURA_CABECALHO_TABELA);
        ctx.fillStyle = "#262626";
        ctx.font = "bold 9px Helvetica, Arial, sans-serif";
        let colX = 0;
        [["Filial", larguras.filial], ["C.Custo", larguras.centro], ["Conta", larguras.conta], ["Valor", larguras.valor]]
            .forEach(([label, largura]) => {
                ctx.fillText(label, colX, y + 12);
                colX += largura;
            });
        y += ALTURA_CABECALHO_TABELA;

        ctx.font = "9px Helvetica, Arial, sans-serif";
        grupo.itens.forEach((item, indice) => {
            if (indice % 2 === 1) {
                ctx.fillStyle = "#f5f5f5";
                ctx.fillRect(0, y, larguraUtil, ALTURA_LINHA_TABELA);
            }
            ctx.fillStyle = "#262626";
            let cx = 0;
            ctx.fillText(ajustarFilial(item.filial), cx, y + 11);
            cx += larguras.filial;

            ctx.fillText(String(item.centroCusto ?? ""), cx, y + 11);
            cx += larguras.centro;

            ctx.fillText(String(item.contaLabel ?? ""), cx, y + 11);
            cx += larguras.conta;

            ctx.fillText(formatarMoedaPdf(item.valor), cx, y + 11);
            y += ALTURA_LINHA_TABELA;
        });

        // Total da nota — soma dos valores de rateio dessa nota fiscal, já calculada em `agruparRateioPorNota`.
        ctx.fillStyle = "#e5e7eb";
        ctx.fillRect(0, y, larguraUtil, ALTURA_LINHA_TOTAL_TABELA);
        ctx.fillStyle = "#262626";
        ctx.font = "bold 9px Helvetica, Arial, sans-serif";
        const larguraLabelTotal = larguras.filial + larguras.centro + larguras.conta;
        ctx.textAlign = "right";
        ctx.fillText("Total", larguraLabelTotal - 4, y + 12);
        ctx.textAlign = "left";
        ctx.fillText(formatarMoedaPdf(grupo.total), larguraLabelTotal, y + 12);
        y += ALTURA_LINHA_TOTAL_TABELA;

        y += ESPACO_ENTRE_NOTAS_TABELA;
    });

    return new Promise(resolve => {
        canvas.toBlob(blob => resolve({ blob, largura: larguraDesenho, altura: alturaDesenho }), "image/png");
    });
}

// --- Forma de Pagamento desenhada como imagem, mesmo mecanismo do rateio. ---
const LARGURA_PADRAO_CARIMBO_PAGAMENTO = 220;
const ALTURA_LINHA_PAGAMENTO = 16;
const MARGEM_VERTICAL_PAGAMENTO = 8;

const TIPOS_CONTA_PAGAMENTO = [{ nome: "Conta Corrente" }, { nome: "Conta Poupança" }];

/** Linhas de texto que o carimbo de forma de pagamento vai mostrar, a partir dos dados preenchidos no diálogo. */
function linhasFormaPagamento(dados) {
    const linhas = [`Forma de Pagamento: ${dados.tipoPagamento || "-"}`];
    if (dados.tipoPagamento === "PIX" && dados.chavePix) {
        linhas.push(`Chave PIX: ${dados.chavePix}`);
    }
    if (dados.tipoPagamento === "Transferência") {
        if (dados.tipoConta) linhas.push(`Tipo de Conta: ${dados.tipoConta}`);
        const agenciaTxt = [dados.agencia, dados.agenciaDigito].filter(Boolean).join("-");
        const contaTxt = [dados.conta, dados.contaDigito].filter(Boolean).join("-");
        if (agenciaTxt) linhas.push(`Agência: ${agenciaTxt}`);
        if (contaTxt) linhas.push(`Conta: ${contaTxt}`);
    }
    return linhas;
}

/** Mesma lógica de `gerarImagemTabelaRateio`, só que pro carimbo (bem menor) de forma de pagamento. */
function gerarImagemFormaPagamento(dados, larguraDesenho) {
    const linhas = linhasFormaPagamento(dados);
    const alturaDesenho = MARGEM_VERTICAL_PAGAMENTO * 2 + linhas.length * ALTURA_LINHA_PAGAMENTO;

    const canvas = document.createElement("canvas");
    canvas.width = larguraDesenho * ESCALA_RASTER_TABELA;
    canvas.height = alturaDesenho * ESCALA_RASTER_TABELA;
    const ctx = canvas.getContext("2d");
    ctx.scale(ESCALA_RASTER_TABELA, ESCALA_RASTER_TABELA);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, larguraDesenho, alturaDesenho);

    let y = MARGEM_VERTICAL_PAGAMENTO;
    linhas.forEach((linha, indice) => {
        ctx.font = indice === 0 ? "bold 10px Helvetica, Arial, sans-serif" : "9px Helvetica, Arial, sans-serif";
        ctx.fillStyle = "#262626";
        ctx.fillText(linha, 0, y + 11);
        y += ALTURA_LINHA_PAGAMENTO;
    });

    return new Promise(resolve => {
        canvas.toBlob(blob => resolve({ blob, largura: larguraDesenho, altura: alturaDesenho }), "image/png");
    });
}

const CAMPOS_FORMA_PAGAMENTO = ["tipoPagamento", "tipoConta", "agencia", "agenciaDigito", "conta", "contaDigito", "chavePix"];

function formaPagamentoInicial(origem) {
    const base = {};
    CAMPOS_FORMA_PAGAMENTO.forEach(campo => { base[campo] = origem?.[campo] || ""; });
    return base;
}

// --- Data de Lançamento — carimbo automático (sempre a data de hoje), sem diálogo: um clique já
// gera e adiciona. Largura sai medida a partir do próprio texto, já que é sempre curto. ---
const ALTURA_CARIMBO_DATA = 24;

function gerarImagemDataLancamento() {
    const texto = `Data de Lançamento: ${new Date().toLocaleDateString("pt-BR")}`;

    const canvasMedida = document.createElement("canvas");
    const ctxMedida = canvasMedida.getContext("2d");
    ctxMedida.font = "bold 10px Helvetica, Arial, sans-serif";
    const larguraDesenho = Math.ceil(ctxMedida.measureText(texto).width) + 4;

    const canvas = document.createElement("canvas");
    canvas.width = larguraDesenho * ESCALA_RASTER_TABELA;
    canvas.height = ALTURA_CARIMBO_DATA * ESCALA_RASTER_TABELA;
    const ctx = canvas.getContext("2d");
    ctx.scale(ESCALA_RASTER_TABELA, ESCALA_RASTER_TABELA);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, larguraDesenho, ALTURA_CARIMBO_DATA);
    ctx.font = "bold 10px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#262626";
    ctx.fillText(texto, 0, 15);

    return new Promise(resolve => {
        canvas.toBlob(blob => resolve({ blob, largura: larguraDesenho, altura: ALTURA_CARIMBO_DATA }), "image/png");
    });
}

// --- Texto Livre — o usuário digita qualquer coisa e ela é desenhada quebrando linha sozinha pra
// caber na largura do carimbo (o <textarea> permite várias linhas, cada uma pode ainda ser mais
// longa que a largura do carimbo). ---
const LARGURA_PADRAO_CARIMBO_TEXTO = 260;
const ALTURA_LINHA_TEXTO = 15;
const MARGEM_TEXTO_LIVRE = 8;
const TAMANHO_FONTE_TEXTO_LIVRE = 10;

/** Quebra cada parágrafo (linha digitada) em várias linhas de desenho, respeitando `larguraUtil`. */
function quebrarEmLinhas(ctx, texto, larguraUtil) {
    const linhas = [];
    texto.split("\n").forEach(paragrafo => {
        if (paragrafo.trim() === "") {
            linhas.push("");
            return;
        }
        let linhaAtual = "";
        paragrafo.split(" ").forEach(palavra => {
            const tentativa = linhaAtual ? `${linhaAtual} ${palavra}` : palavra;
            if (linhaAtual && ctx.measureText(tentativa).width > larguraUtil) {
                linhas.push(linhaAtual);
                linhaAtual = palavra;
            } else {
                linhaAtual = tentativa;
            }
        });
        linhas.push(linhaAtual);
    });
    return linhas;
}

function gerarImagemTextoLivre(texto, larguraDesenho) {
    const larguraUtil = larguraDesenho - MARGEM_TEXTO_LIVRE * 2;
    const fonte = `${TAMANHO_FONTE_TEXTO_LIVRE}px Helvetica, Arial, sans-serif`;

    const canvasMedida = document.createElement("canvas");
    const ctxMedida = canvasMedida.getContext("2d");
    ctxMedida.font = fonte;
    const linhas = quebrarEmLinhas(ctxMedida, texto, larguraUtil);

    const alturaDesenho = MARGEM_TEXTO_LIVRE * 2 + linhas.length * ALTURA_LINHA_TEXTO;

    const canvas = document.createElement("canvas");
    canvas.width = larguraDesenho * ESCALA_RASTER_TABELA;
    canvas.height = alturaDesenho * ESCALA_RASTER_TABELA;
    const ctx = canvas.getContext("2d");
    ctx.scale(ESCALA_RASTER_TABELA, ESCALA_RASTER_TABELA);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, larguraDesenho, alturaDesenho);
    ctx.font = fonte;
    ctx.fillStyle = "#262626";

    let y = MARGEM_TEXTO_LIVRE;
    linhas.forEach(linha => {
        ctx.fillText(linha, MARGEM_TEXTO_LIVRE, y + 11);
        y += ALTURA_LINHA_TEXTO;
    });

    return new Promise(resolve => {
        canvas.toBlob(blob => resolve({ blob, largura: larguraDesenho, altura: alturaDesenho }), "image/png");
    });
}

/**
 * Núcleo reutilizável de assinatura de PDF: recebe o PDF já em memória (Blob/File) — pode vir de
 * um anexo já existente no backend (AssinarAnexoModal) ou de um arquivo ainda só selecionado
 * localmente, antes de a solicitação existir (StagedAnexosEditor) — e devolve o PDF final (com os
 * carimbos escolhidos já embutidos nos bytes) via onConfirmar. Quem chama decide o que fazer com o
 * resultado: subir como novo anexo, ou apenas trocar o arquivo ainda em memória.
 *
 * Cinco tipos de carimbo totalmente independentes podem ser adicionados sobre a página —
 * Assinatura (imagem cadastrada em Meu Usuário), Rateio Orçamentário (tabela das notas fiscais
 * escolhidas), Forma de Pagamento (dados digitados no próprio diálogo, pré-preenchidos com o que a
 * solicitação já tiver via `formaPagamento`), Data de Lançamento (a data de hoje, sem diálogo — um
 * clique já carimba) e Texto Livre (qualquer texto digitado pelo usuário) — e cada tipo pode ter
 * QUALQUER QUANTIDADE de instâncias na mesma página (ex.: duas assinaturas em pontos diferentes do
 * documento), cada uma movida, redimensionada e removida sem afetar as demais.
 *
 * Documentos com mais de uma página: trocar de página salva automaticamente os carimbos da página
 * que está sendo deixada (não é preciso confirmar pra não perdê-los) — "Salvar e Fechar" embute os
 * carimbos de todas as páginas onde algo foi colocado, não só da página exibida no momento.
 *
 * `documento` é o gatilho de abertura: null fecha o modal; { titulo, nomeArquivoSaida, blob }
 * abre — `blob` pode chegar null por um instante se quem chama ainda estiver buscando o PDF
 * (ex: baixando do backend), e o modal mostra o carregamento normalmente.
 */
export default function AssinarPdfModal({ documento, erroCarregar, onClose, onConfirmar, rateioItens, rateioItensDetalhados, formaPagamento }) {
    const [carregandoAssinatura, setCarregandoAssinatura] = useState(true);
    const [semAssinaturaCadastrada, setSemAssinaturaCadastrada] = useState(false);
    const [erro, setErro] = useState(null);
    const [enviando, setEnviando] = useState(false);

    const [numPaginas, setNumPaginas] = useState(0);
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [tamanhoCanvas, setTamanhoCanvas] = useState({ width: 0, height: 0 });
    const [assinaturaUrl, setAssinaturaUrl] = useState(null);
    const [renderizandoPagina, setRenderizandoPagina] = useState(false);

    // Cada tipo de carimbo é uma LISTA — o usuário pode adicionar quantas instâncias quiser de cada
    // um (ex.: várias assinaturas em pontos diferentes da mesma página), cada uma com sua própria
    // caixa (posição/tamanho) e, exceto a assinatura (sempre a mesma imagem), sua própria imagem gerada.
    const [assinaturas, setAssinaturas] = useState([]); // [{ id, caixa }]
    const [rateios, setRateios] = useState([]); // [{ id, caixa, blob, url, aspectRatio }]
    const [pagamentos, setPagamentos] = useState([]); // [{ id, caixa, blob, url, aspectRatio }]
    const [datas, setDatas] = useState([]); // [{ id, caixa, blob, url, aspectRatio }]
    const [textos, setTextos] = useState([]); // [{ id, caixa, blob, url, aspectRatio }]
    const proximoIdRef = useRef(0);

    const [confirmandoRateio, setConfirmandoRateio] = useState(false);
    const [notasFiscaisSelecionadas, setNotasFiscaisSelecionadas] = useState([]);
    const [gerandoImagemRateio, setGerandoImagemRateio] = useState(false);

    const [confirmandoPagamento, setConfirmandoPagamento] = useState(false);
    const [pagamentoForm, setPagamentoForm] = useState(null);
    const [tiposPagamento, setTiposPagamento] = useState([]);
    const [gerandoImagemPagamento, setGerandoImagemPagamento] = useState(false);

    const [gerandoImagemData, setGerandoImagemData] = useState(false);

    const [confirmandoTexto, setConfirmandoTexto] = useState(false);
    const [textoLivre, setTextoLivre] = useState("");
    const [gerandoImagemTexto, setGerandoImagemTexto] = useState(false);

    // PDF protegido por senha: o pdf.js chama onPassword em vez de abrir — guardamos a função de
    // resposta numa ref e mostramos o diálogo; a promise de abertura fica pendente até responder.
    const [pedindoSenha, setPedindoSenha] = useState(false);
    const [senhaIncorreta, setSenhaIncorreta] = useState(false);
    const [senhaDigitada, setSenhaDigitada] = useState("");
    const respostaSenhaRef = useRef(null);
    const tarefaPdfRef = useRef(null);

    // Carimbos já salvos de páginas diferentes da atual — trocar de página salva sozinho os
    // carimbos da página que está sendo deixada aqui (ver `irParaPagina`), pra dar pra assinar mais
    // de uma página do mesmo PDF sem perder o que já foi feito. Cada entrada guarda a escala/altura
    // do canvas de quando foi salva (páginas podem ter tamanhos diferentes entre si).
    const [carimbosPorPagina, setCarimbosPorPagina] = useState({});
    const carimbosPorPaginaRef = useRef({});
    useEffect(() => { carimbosPorPaginaRef.current = carimbosPorPagina; }, [carimbosPorPagina]);

    const canvasRef = useRef(null);
    const pdfDocRef = useRef(null);
    const renderTaskRef = useRef(null);
    const assinaturaBlobRef = useRef(null);
    const assinaturaUrlRef = useRef(null);
    const assinaturaContentTypeRef = useRef(null);
    const aspectRatioRef = useRef(0.4);
    const escalaRef = useRef(1);
    const modoRef = useRef(null);
    const alvoInteracaoRef = useRef(null);
    const ultimaPosRef = useRef({ x: 0, y: 0 });

    const blob = documento?.blob ?? null;
    const erroFinal = erro || erroCarregar;
    const temRateioDisponivel = rateioItens?.length > 0;

    // Notas fiscais distintas presentes no rateio da solicitação — o usuário marca quais delas
    // correspondem a este documento específico. É seleção múltipla porque um único arquivo pode
    // reunir várias notas no mesmo PDF; a mesma solicitação pode ter rateio de notas que não têm
    // nada a ver com o documento sendo assinado agora.
    const notasFiscaisDisponiveis = useMemo(() => {
        const vistas = new Set();
        return (rateioItensDetalhados ?? []).reduce((acc, item) => {
            const numero = chaveNota(item);
            if (!vistas.has(numero)) {
                vistas.add(numero);
                acc.push({ value: numero, label: numero });
            }
            return acc;
        }, []);
    }, [rateioItensDetalhados]);

    const itensRateioDasNotasSelecionadas = useMemo(
        () => (rateioItensDetalhados ?? []).filter(item => notasFiscaisSelecionadas.includes(chaveNota(item))),
        [rateioItensDetalhados, notasFiscaisSelecionadas]
    );

    function alternarNotaFiscal(numero) {
        setNotasFiscaisSelecionadas(atual =>
            atual.includes(numero) ? atual.filter(n => n !== numero) : [...atual, numero]
        );
    }

    // O <canvas> só existe no DOM quando `carregando` é false — como isso também depende da
    // assinatura do usuário (uma busca independente, com timing próprio), o efeito de renderização
    // da página precisa reagir a essa mudança também, não só a paginaAtual/numPaginas, senão pode
    // rodar antes do canvas existir (quando o PDF termina de carregar antes da assinatura) e nunca
    // mais tentar de novo.
    const carregando = !erroFinal && !semAssinaturaCadastrada && (carregandoAssinatura || !blob || numPaginas === 0);

    const temAlgumCarimboAgoraRef = useRef(false);
    useEffect(() => {
        temAlgumCarimboAgoraRef.current = assinaturas.length > 0 || rateios.length > 0 || pagamentos.length > 0
            || datas.length > 0 || textos.length > 0;
    }, [assinaturas, rateios, pagamentos, datas, textos]);

    // Busca o catálogo de Tipos de Pagamento uma única vez — usado no diálogo de Forma de
    // Pagamento, mesmo dropdown já usado no cadastro de Lançamento de Despesa.
    useEffect(() => {
        tipoPagamentoService.listar().then(res => setTiposPagamento(res.data)).catch(() => setTiposPagamento([]));
    }, []);

    // Rede de segurança: algumas falhas internas do pdf.js (ex.: fontes embutidas malformadas em
    // PDFs de geradores legados) rejeitam uma promise própria, fora da cadeia que já tratamos com
    // try/catch — sem isso, o usuário só via uma área em branco, sem nenhuma mensagem. Só mostra o
    // erro se a página ainda não tiver renderizado com sucesso, pra não sobrepor um render que deu
    // certo com ruído de uma rejeição solta e não relacionada.
    useEffect(() => {
        if (!documento) return undefined;
        function aoRejeitarSemTratar(event) {
            if (temAlgumCarimboAgoraRef.current) return;
            // Pedir/errar a senha de um PDF protegido faz o pdf.js rejeitar uma promise interna com
            // PasswordException — é o fluxo normal (o diálogo de senha cuida disso), não um erro de renderização.
            if (event.reason?.name === "PasswordException") return;
            console.error("Erro não tratado ao processar o PDF para assinatura", event.reason);
            setErro("Não foi possível renderizar este PDF.");
            setRenderizandoPagina(false);
        }
        window.addEventListener("unhandledrejection", aoRejeitarSemTratar);
        return () => window.removeEventListener("unhandledrejection", aoRejeitarSemTratar);
    }, [documento]);

    // Verifica/baixa a assinatura salva do usuário sempre que o modal é aberto — independe do PDF
    // (ex: no fluxo de anexo do backend, o PDF ainda pode estar sendo baixado nesse momento).
    useEffect(() => {
        if (!documento) return undefined;
        let cancelado = false;

        setCarregandoAssinatura(true);
        setSemAssinaturaCadastrada(false);
        setErro(null);

        meuUsuarioService.buscarAssinatura()
            .then(async res => {
                if (cancelado) return;
                if (!res.data?.existe) {
                    setSemAssinaturaCadastrada(true);
                    return;
                }
                assinaturaContentTypeRef.current = res.data.contentType;

                const imagemRes = await meuUsuarioService.baixarAssinaturaImagem();
                if (cancelado) return;
                assinaturaBlobRef.current = imagemRes.data;
                assinaturaUrlRef.current = URL.createObjectURL(imagemRes.data);
                setAssinaturaUrl(assinaturaUrlRef.current);

                const imagemPreview = new Image();
                imagemPreview.onload = () => {
                    aspectRatioRef.current = imagemPreview.naturalHeight / imagemPreview.naturalWidth;
                };
                imagemPreview.src = assinaturaUrlRef.current;
            })
            .catch(error => {
                console.error("Erro ao carregar sua assinatura", error);
                if (!cancelado) setErro("Não foi possível carregar sua assinatura.");
            })
            .finally(() => {
                if (!cancelado) setCarregandoAssinatura(false);
            });

        return () => {
            cancelado = true;
            if (assinaturaUrlRef.current) {
                URL.revokeObjectURL(assinaturaUrlRef.current);
                assinaturaUrlRef.current = null;
            }
        };
    }, [documento]);

    // Libera as URLs de imagem de todas as instâncias (rateio/pagamento/data/texto) ainda na tela —
    // a assinatura não entra aqui porque reaproveita `assinaturaUrl`, uma única URL pra todas as
    // instâncias, liberada à parte no efeito acima.
    function revogarUrlsAtuais() {
        rateios.forEach(r => URL.revokeObjectURL(r.url));
        pagamentos.forEach(p => URL.revokeObjectURL(p.url));
        datas.forEach(d => URL.revokeObjectURL(d.url));
        textos.forEach(t => URL.revokeObjectURL(t.url));
    }

    // Cópias sempre atualizadas das listas acima, só pro cleanup de desmontagem abaixo — esse
    // cleanup só roda uma vez (array de dependências vazio), então precisa ler o estado mais
    // recente por uma ref; se lesse as listas direto, ficaria preso pra sempre nos valores (vazios)
    // da primeira renderização.
    const rateiosRef = useRef([]);
    useEffect(() => { rateiosRef.current = rateios; }, [rateios]);
    const pagamentosRef = useRef([]);
    useEffect(() => { pagamentosRef.current = pagamentos; }, [pagamentos]);
    const datasRef = useRef([]);
    useEffect(() => { datasRef.current = datas; }, [datas]);
    const textosRef = useRef([]);
    useEffect(() => { textosRef.current = textos; }, [textos]);

    // Libera tudo se o componente desmontar com o modal ainda aberto.
    useEffect(() => {
        return () => {
            rateiosRef.current.forEach(r => URL.revokeObjectURL(r.url));
            pagamentosRef.current.forEach(p => URL.revokeObjectURL(p.url));
            datasRef.current.forEach(d => URL.revokeObjectURL(d.url));
            textosRef.current.forEach(t => URL.revokeObjectURL(t.url));
        };
    }, []);

    // Abre o PDF em si assim que o blob estiver disponível (pode chegar depois da assinatura, se
    // quem chama ainda estiver buscando o arquivo) — e reseta tudo, já que pertence a um documento novo.
    useEffect(() => {
        pdfDocRef.current = null;
        setNumPaginas(0);
        setTamanhoCanvas({ width: 0, height: 0 });
        revogarUrlsAtuais();
        setAssinaturas([]);
        setRateios([]);
        setPagamentos([]);
        setDatas([]);
        setTextos([]);
        setNotasFiscaisSelecionadas([]);
        setPagamentoForm(null);
        setTextoLivre("");
        setCarimbosPorPagina({});
        if (!blob) return undefined;

        let cancelado = false;
        blob.arrayBuffer()
            .then(buffer => {
                const tarefa = getDocument({
                    data: new Uint8Array(buffer),
                    cMapUrl: CMAP_URL,
                    cMapPacked: true,
                    standardFontDataUrl: STANDARD_FONT_DATA_URL,
                    // Alguns PDFs (comuns em geradores de nota fiscal legados) embutem subconjuntos de
                    // fonte TrueType malformados — carregá-los via FontFace nativa do navegador dispara
                    // uma rejeição de promise fora da nossa cadeia (não capturável por try/catch normal)
                    // e o documento nunca renderiza. Desligar o FontFace nativo faz o pdf.js rasterizar
                    // os glifos ele mesmo, bem mais tolerante a fontes malformadas.
                    disableFontFace: true,
                });
                // Protegido por senha: pede ao usuário (de novo, se errar). 2 = PasswordResponses.INCORRECT_PASSWORD.
                tarefa.onPassword = (responder, motivo) => {
                    if (cancelado) return;
                    respostaSenhaRef.current = responder;
                    setSenhaIncorreta(motivo === 2);
                    setSenhaDigitada("");
                    setPedindoSenha(true);
                };
                tarefaPdfRef.current = tarefa;
                return tarefa.promise;
            })
            .then(pdfDoc => {
                if (cancelado) return;
                pdfDocRef.current = pdfDoc;
                setNumPaginas(pdfDoc.numPages);
                setPaginaAtual(1);
            })
            .catch(error => {
                console.error("Erro ao abrir o PDF para assinatura", error);
                if (!cancelado) setErro("Não foi possível abrir o PDF para assinatura.");
            });

        return () => {
            cancelado = true;
            setPedindoSenha(false);
            tarefaPdfRef.current?.destroy();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blob]);

    // Renderiza a página atual num canvas em resolução 1:1 com o CSS, para simplificar a
    // conversão de coordenadas (pixel do canvas -> ponto do PDF) na hora de confirmar.
    useEffect(() => {
        // `carregando` também depende da busca (independente) da assinatura do usuário — o <canvas>
        // só existe no DOM quando ela termina de carregar. Sem essa dependência aqui, se o PDF
        // terminar de carregar ANTES da assinatura, este efeito roda cedo demais, acha
        // `canvasRef.current` nulo, desiste, e nunca mais tenta de novo (deps não mudam de novo).
        if (carregando) return undefined;
        const pdfDoc = pdfDocRef.current;
        if (!pdfDoc || !canvasRef.current) return undefined;
        let cancelado = false;

        // Cancela qualquer render anterior ainda em andamento antes de iniciar um novo no mesmo
        // canvas — sem isso, trocar de página rápido lança "Cannot use the same canvas..." do pdf.js.
        renderTaskRef.current?.cancel();
        setRenderizandoPagina(true);

        pdfDoc.getPage(paginaAtual)
            .then(page => {
                if (cancelado) return null;
                const viewportBase = page.getViewport({ scale: 1 });
                const escala = Math.min(LARGURA_MAXIMA_PAGINA / viewportBase.width, 1.5);
                const viewport = page.getViewport({ scale: escala });
                escalaRef.current = escala;

                const canvas = canvasRef.current;
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                setTamanhoCanvas({ width: viewport.width, height: viewport.height });

                const contexto = canvas.getContext("2d");
                const tarefa = page.render({ canvasContext: contexto, viewport });
                renderTaskRef.current = tarefa;
                return tarefa.promise;
            })
            .catch(error => {
                if (error?.name === "RenderingCancelledException") return;
                console.error("Erro ao renderizar página do PDF", error);
                if (!cancelado) setErro("Não foi possível renderizar esta página do PDF.");
            })
            .finally(() => {
                if (!cancelado) {
                    setRenderizandoPagina(false);
                    restaurarCarimbosSalvos(paginaAtual);
                }
            });

        return () => {
            cancelado = true;
            renderTaskRef.current?.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginaAtual, numPaginas, carregando]);

    // Estado dos carimbos da página exibida agora, no formato guardado por página (inclui a
    // escala/altura do canvas do momento, necessárias pra converter a posição em pixel de volta pra
    // ponto de PDF corretamente mesmo depois de trocar de página).
    function capturarEstadoAtual() {
        const escala = escalaRef.current;
        const alturaCanvas = tamanhoCanvas.height;
        return {
            assinaturas: assinaturas.map(a => ({ caixa: a.caixa, escala, alturaCanvas })),
            rateios: rateios.map(r => ({ caixa: r.caixa, escala, alturaCanvas, blob: r.blob })),
            pagamentos: pagamentos.map(p => ({ caixa: p.caixa, escala, alturaCanvas, blob: p.blob })),
            datas: datas.map(d => ({ caixa: d.caixa, escala, alturaCanvas, blob: d.blob })),
            textos: textos.map(t => ({ caixa: t.caixa, escala, alturaCanvas, blob: t.blob })),
        };
    }

    function paginaTemAlgo() {
        return assinaturas.length > 0 || rateios.length > 0 || pagamentos.length > 0
            || datas.length > 0 || textos.length > 0;
    }

    // Guarda (ou remove, se a página ficou vazia) os carimbos de uma página no mapa por página.
    function salvarEstadoDaPagina(pagina) {
        const temAlgo = paginaTemAlgo();
        setCarimbosPorPagina(atual => {
            if (!temAlgo) {
                if (!(pagina in atual)) return atual;
                const copia = { ...atual };
                delete copia[pagina];
                return copia;
            }
            return { ...atual, [pagina]: capturarEstadoAtual() };
        });
    }

    // Traz de volta pra tela os carimbos já salvos de uma página (ex.: ao voltar pra ela depois de
    // ter passado por outra) — assim dá pra conferir/ajustar antes de trocar de página de novo.
    function restaurarCarimbosSalvos(pagina) {
        const salvo = carimbosPorPaginaRef.current[pagina];
        if (!salvo) return;

        function paraLista(itens) {
            return itens.map(item => ({
                id: proximoIdRef.current++,
                caixa: item.caixa,
                blob: item.blob,
                url: item.blob ? URL.createObjectURL(item.blob) : undefined,
                aspectRatio: item.caixa.height / item.caixa.width,
            }));
        }

        setAssinaturas(salvo.assinaturas.map(a => ({ id: proximoIdRef.current++, caixa: a.caixa })));
        setRateios(paraLista(salvo.rateios));
        setPagamentos(paraLista(salvo.pagamentos));
        setDatas(paraLista(salvo.datas));
        setTextos(paraLista(salvo.textos));
    }

    // Trocar de página salva sozinho os carimbos da página que está sendo deixada — não existe mais
    // um botão manual de "salvar página", a troca de página já é o gatilho.
    function irParaPagina(pagina) {
        salvarEstadoDaPagina(paginaAtual);
        revogarUrlsAtuais();
        setPaginaAtual(pagina);
        setAssinaturas([]);
        setRateios([]);
        setPagamentos([]);
        setDatas([]);
        setTextos([]);
    }

    // Onde entra um carimbo novo: embaixo do mais baixo que já existir (empilhando na ordem em que
    // forem adicionados, inclusive repetidos do mesmo tipo), ou no canto padrão se a página ainda
    // estiver vazia.
    function proximaPosicaoEmpilhada(altura) {
        const existentes = [
            ...assinaturas.map(a => a.caixa),
            ...rateios.map(r => r.caixa),
            ...pagamentos.map(p => p.caixa),
            ...datas.map(d => d.caixa),
            ...textos.map(t => t.caixa),
        ];
        if (existentes.length === 0) {
            return { x: MARGEM_PADRAO, y: MARGEM_PADRAO };
        }
        const maisBaixo = existentes.reduce((acc, c) => (c.y + c.height > acc.y + acc.height ? c : acc));
        const y = Math.min(
            maisBaixo.y + maisBaixo.height + ESPACO_ENTRE_CARIMBOS,
            Math.max(MARGEM_PADRAO, tamanhoCanvas.height - altura - MARGEM_PADRAO)
        );
        return { x: MARGEM_PADRAO, y };
    }

    // `tipo`+`id` dizem qual instância está sendo manipulada — cada carimbo (de qualquer tipo, e
    // pode haver vários do mesmo tipo) é um overlay independente sobre a mesma página, cada um com
    // sua caixa e proporção próprias, mas todos compartilham a mesma mecânica de arrastar/redimensionar.
    function iniciarMover(tipo, id) {
        return e => {
            e.stopPropagation();
            modoRef.current = "mover";
            alvoInteracaoRef.current = { tipo, id };
            ultimaPosRef.current = { x: e.clientX, y: e.clientY };
            e.currentTarget.setPointerCapture(e.pointerId);
        };
    }

    function iniciarRedimensionar(tipo, id) {
        return e => {
            e.stopPropagation();
            modoRef.current = "redimensionar";
            alvoInteracaoRef.current = { tipo, id };
            ultimaPosRef.current = { x: e.clientX, y: e.clientY };
            e.currentTarget.setPointerCapture(e.pointerId);
        };
    }

    const CONFIG_TIPO_CARIMBO = {
        rateio: () => ({ setLista: setRateios, aspectRatioPadrao: 1, tamanhoMinimo: TAMANHO_MINIMO_RATEIO }),
        pagamento: () => ({ setLista: setPagamentos, aspectRatioPadrao: 1, tamanhoMinimo: TAMANHO_MINIMO_PAGAMENTO }),
        data: () => ({ setLista: setDatas, aspectRatioPadrao: 1, tamanhoMinimo: TAMANHO_MINIMO_DATA }),
        texto: () => ({ setLista: setTextos, aspectRatioPadrao: 1, tamanhoMinimo: TAMANHO_MINIMO_TEXTO }),
        assinatura: () => ({ setLista: setAssinaturas, aspectRatioPadrao: aspectRatioRef.current, tamanhoMinimo: TAMANHO_MINIMO_ASSINATURA }),
    };

    function moverOuRedimensionar(e) {
        if (!modoRef.current || !alvoInteracaoRef.current) return;
        const dx = e.clientX - ultimaPosRef.current.x;
        const dy = e.clientY - ultimaPosRef.current.y;
        ultimaPosRef.current = { x: e.clientX, y: e.clientY };

        const { tipo, id } = alvoInteracaoRef.current;
        const config = (CONFIG_TIPO_CARIMBO[tipo] ?? CONFIG_TIPO_CARIMBO.assinatura)();

        config.setLista(atual => atual.map(item => {
            if (item.id !== id) return item;
            const c = item.caixa;
            if (modoRef.current === "mover") {
                const x = Math.min(Math.max(0, c.x + dx), Math.max(0, tamanhoCanvas.width - c.width));
                const y = Math.min(Math.max(0, c.y + dy), Math.max(0, tamanhoCanvas.height - c.height));
                return { ...item, caixa: { ...c, x, y } };
            }

            const aspectRatio = item.aspectRatio ?? config.aspectRatioPadrao;
            const larguraMaxPorX = tamanhoCanvas.width - c.x;
            const larguraMaxPorY = (tamanhoCanvas.height - c.y) / aspectRatio;
            const larguraMax = Math.min(larguraMaxPorX, larguraMaxPorY);
            const novaLargura = Math.min(Math.max(config.tamanhoMinimo, c.width + dx), larguraMax);
            return { ...item, caixa: { ...c, width: novaLargura, height: novaLargura * aspectRatio } };
        }));
    }

    function pararInteracao() {
        modoRef.current = null;
        alvoInteracaoRef.current = null;
    }

    // --- Assinatura ---
    function adicionarAssinatura() {
        const largura = Math.min(LARGURA_PADRAO_ASSINATURA, tamanhoCanvas.width * 0.4);
        const altura = largura * (aspectRatioRef.current || 0.4);
        // A primeira assinatura da página nasce no canto inferior direito (ponto de assinatura mais
        // comum); a partir da segunda (ou se já tiver outro carimbo na página), empilha embaixo do
        // que já existir, pra não nascer sempre em cima de outro carimbo.
        const pos = paginaTemAlgo()
            ? proximaPosicaoEmpilhada(altura)
            : { x: Math.max(MARGEM_PADRAO, tamanhoCanvas.width - largura - MARGEM_PADRAO), y: Math.max(MARGEM_PADRAO, tamanhoCanvas.height - altura - MARGEM_PADRAO) };
        setAssinaturas(atual => [...atual, { id: proximoIdRef.current++, caixa: { x: pos.x, y: pos.y, width: largura, height: altura } }]);
    }

    function removerAssinatura(id) {
        setAssinaturas(atual => atual.filter(a => a.id !== id));
    }

    // --- Rateio ---
    function abrirDialogoRateio() {
        if (!notasFiscaisSelecionadas.length && notasFiscaisDisponiveis.length === 1) {
            setNotasFiscaisSelecionadas([notasFiscaisDisponiveis[0].value]);
        }
        setConfirmandoRateio(true);
    }

    function removerRateio(id) {
        setRateios(atual => {
            const alvo = atual.find(r => r.id === id);
            if (alvo) URL.revokeObjectURL(alvo.url);
            return atual.filter(r => r.id !== id);
        });
    }

    // Gera a imagem da tabela de rateio (só das notas marcadas) e adiciona mais uma instância de
    // carimbo de rateio na página — não substitui nenhuma já existente, o usuário pode ter quantas
    // tabelas de rateio quiser na mesma página.
    async function confirmarNotasRateio() {
        setGerandoImagemRateio(true);
        try {
            const itensDasNotasSelecionadas = rateioItens.filter(item => notasFiscaisSelecionadas.includes(chaveNota(item)));
            const larguraMaximaDisponivel = Math.max(TAMANHO_MINIMO_RATEIO, tamanhoCanvas.width - MARGEM_PADRAO * 2);
            const larguraDesenho = Math.min(LARGURA_PADRAO_TABELA_RATEIO, larguraMaximaDisponivel);
            const { blob: blobRateio, largura, altura } = await gerarImagemTabelaRateio(itensDasNotasSelecionadas, larguraDesenho);

            const pos = proximaPosicaoEmpilhada(altura);
            const url = URL.createObjectURL(blobRateio);
            setRateios(atual => [...atual, {
                id: proximoIdRef.current++,
                caixa: { x: pos.x, y: pos.y, width: largura, height: altura },
                blob: blobRateio,
                url,
                aspectRatio: altura / largura,
            }]);

            setConfirmandoRateio(false);
        } catch (error) {
            console.error("Erro ao gerar a imagem da tabela de rateio", error);
            toast.error("Não foi possível gerar a imagem do rateio.");
        } finally {
            setGerandoImagemRateio(false);
        }
    }

    // --- Forma de pagamento ---
    function abrirDialogoPagamento() {
        setPagamentoForm(atual => atual ?? formaPagamentoInicial(formaPagamento));
        setConfirmandoPagamento(true);
    }

    function removerPagamento(id) {
        setPagamentos(atual => {
            const alvo = atual.find(p => p.id === id);
            if (alvo) URL.revokeObjectURL(alvo.url);
            return atual.filter(p => p.id !== id);
        });
    }

    async function confirmarFormaPagamento() {
        setGerandoImagemPagamento(true);
        try {
            const larguraMaximaDisponivel = Math.max(TAMANHO_MINIMO_PAGAMENTO, tamanhoCanvas.width - MARGEM_PADRAO * 2);
            const larguraDesenho = Math.min(LARGURA_PADRAO_CARIMBO_PAGAMENTO, larguraMaximaDisponivel);
            const { blob: blobPagamento, largura, altura } = await gerarImagemFormaPagamento(pagamentoForm, larguraDesenho);

            const pos = proximaPosicaoEmpilhada(altura);
            const url = URL.createObjectURL(blobPagamento);
            setPagamentos(atual => [...atual, {
                id: proximoIdRef.current++,
                caixa: { x: pos.x, y: pos.y, width: largura, height: altura },
                blob: blobPagamento,
                url,
                aspectRatio: altura / largura,
            }]);

            setConfirmandoPagamento(false);
        } catch (error) {
            console.error("Erro ao gerar a imagem da forma de pagamento", error);
            toast.error("Não foi possível gerar o carimbo da forma de pagamento.");
        } finally {
            setGerandoImagemPagamento(false);
        }
    }

    // --- Data de Lançamento --- (sem diálogo — um clique já gera e adiciona a data de hoje)
    async function adicionarDataLancamento() {
        setGerandoImagemData(true);
        try {
            const { blob: blobData, largura, altura } = await gerarImagemDataLancamento();
            const pos = proximaPosicaoEmpilhada(altura);
            const url = URL.createObjectURL(blobData);
            setDatas(atual => [...atual, {
                id: proximoIdRef.current++,
                caixa: { x: pos.x, y: pos.y, width: largura, height: altura },
                blob: blobData,
                url,
                aspectRatio: altura / largura,
            }]);
        } catch (error) {
            console.error("Erro ao gerar o carimbo de data de lançamento", error);
            toast.error("Não foi possível gerar o carimbo de data.");
        } finally {
            setGerandoImagemData(false);
        }
    }

    function removerData(id) {
        setDatas(atual => {
            const alvo = atual.find(d => d.id === id);
            if (alvo) URL.revokeObjectURL(alvo.url);
            return atual.filter(d => d.id !== id);
        });
    }

    // --- Texto Livre ---
    function abrirDialogoTexto() {
        setTextoLivre("");
        setConfirmandoTexto(true);
    }

    function removerTexto(id) {
        setTextos(atual => {
            const alvo = atual.find(t => t.id === id);
            if (alvo) URL.revokeObjectURL(alvo.url);
            return atual.filter(t => t.id !== id);
        });
    }

    async function confirmarTextoLivre() {
        setGerandoImagemTexto(true);
        try {
            const larguraMaximaDisponivel = Math.max(TAMANHO_MINIMO_TEXTO, tamanhoCanvas.width - MARGEM_PADRAO * 2);
            const larguraDesenho = Math.min(LARGURA_PADRAO_CARIMBO_TEXTO, larguraMaximaDisponivel);
            const { blob: blobTexto, largura, altura } = await gerarImagemTextoLivre(textoLivre, larguraDesenho);

            const pos = proximaPosicaoEmpilhada(altura);
            const url = URL.createObjectURL(blobTexto);
            setTextos(atual => [...atual, {
                id: proximoIdRef.current++,
                caixa: { x: pos.x, y: pos.y, width: largura, height: altura },
                blob: blobTexto,
                url,
                aspectRatio: altura / largura,
            }]);

            setConfirmandoTexto(false);
        } catch (error) {
            console.error("Erro ao gerar a imagem do texto livre", error);
            toast.error("Não foi possível gerar o carimbo de texto.");
        } finally {
            setGerandoImagemTexto(false);
        }
    }

    // O pdf-lib não descriptografa: PDF protegido (por senha ou restrições) faz o load lançar
    // EncryptedPDFError. Nesse caso reconstruímos o documento a partir das páginas já abertas pelo
    // pdf.js (que aceita a senha digitada), uma imagem por página — o texto deixa de ser
    // selecionável e o arquivo gerado sai sem a proteção, mas os carimbos entram normalmente.
    async function carregarPdfParaEdicao() {
        const bytesPdf = await blob.arrayBuffer();
        try {
            return await PDFDocument.load(bytesPdf);
        } catch (error) {
            if (error?.name !== "EncryptedPDFError" && !/encrypted/i.test(error?.message ?? "")) throw error;
        }

        const pdfDoc = pdfDocRef.current;
        const novo = await PDFDocument.create();
        for (let i = 1; i <= pdfDoc.numPages; i++) {
            const page = await pdfDoc.getPage(i);
            const base = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: 2 });
            const canvasPagina = document.createElement("canvas");
            canvasPagina.width = viewport.width;
            canvasPagina.height = viewport.height;
            await page.render({ canvasContext: canvasPagina.getContext("2d"), viewport }).promise;
            const jpeg = await new Promise(resolve => canvasPagina.toBlob(resolve, "image/jpeg", 0.92));
            const imagem = await novo.embedJpg(await jpeg.arrayBuffer());
            const paginaNova = novo.addPage([base.width, base.height]);
            paginaNova.drawImage(imagem, { x: 0, y: 0, width: base.width, height: base.height });
        }
        return novo;
    }

    // --- Confirmação final ---
    // Embute os carimbos de TODAS as páginas onde algo foi colocado — as que já foram salvas ao
    // trocar de página (ver `irParaPagina`) mais a página exibida agora, mesmo que o usuário nunca
    // tenha saído dela (nesse caso ela nunca chegou a ser salva no mapa por página).
    async function confirmarDocumento() {
        if (!blob) return;

        const carimbosFinal = { ...carimbosPorPagina };
        if (paginaTemAlgo()) {
            carimbosFinal[paginaAtual] = capturarEstadoAtual();
        } else {
            delete carimbosFinal[paginaAtual];
        }

        const paginasComCarimbo = Object.entries(carimbosFinal).filter(
            ([, c]) => c.assinaturas.length > 0 || c.rateios.length > 0 || c.pagamentos.length > 0
                || c.datas.length > 0 || c.textos.length > 0
        );

        if (paginasComCarimbo.length === 0) {
            toast.error("Adicione ao menos um carimbo (assinatura, rateio, forma de pagamento, data ou texto) antes de confirmar.");
            return;
        }

        setEnviando(true);
        try {
            const pdfDocLib = await carregarPdfParaEdicao();

            function desenhar(pagina, entrada, imagemEmbutida) {
                pagina.drawImage(imagemEmbutida, {
                    x: entrada.caixa.x / entrada.escala,
                    y: (entrada.alturaCanvas - entrada.caixa.y - entrada.caixa.height) / entrada.escala,
                    width: entrada.caixa.width / entrada.escala,
                    height: entrada.caixa.height / entrada.escala,
                });
            }

            async function desenharTodosDaLista(pagina, lista) {
                for (const entrada of lista) {
                    const bytes = await entrada.blob.arrayBuffer();
                    const imagem = await pdfDocLib.embedPng(bytes);
                    desenhar(pagina, entrada, imagem);
                }
            }

            // A assinatura é sempre a mesma imagem — embute uma vez só e reaproveita em toda
            // instância/página que a usar, em vez de reprocessar os mesmos bytes repetidamente.
            let imagemAssinaturaEmbutida = null;
            const precisaAssinatura = paginasComCarimbo.some(([, c]) => c.assinaturas.length > 0);
            if (precisaAssinatura && assinaturaBlobRef.current) {
                const bytesAssinatura = await assinaturaBlobRef.current.arrayBuffer();
                imagemAssinaturaEmbutida = assinaturaContentTypeRef.current === "image/jpeg"
                    ? await pdfDocLib.embedJpg(bytesAssinatura)
                    : await pdfDocLib.embedPng(bytesAssinatura);
            }

            for (const [numeroPaginaTexto, carimbo] of paginasComCarimbo) {
                const pagina = pdfDocLib.getPages()[Number(numeroPaginaTexto) - 1];
                if (!pagina) continue;

                for (const entrada of carimbo.assinaturas) {
                    if (imagemAssinaturaEmbutida) desenhar(pagina, entrada, imagemAssinaturaEmbutida);
                }
                await desenharTodosDaLista(pagina, carimbo.rateios);
                await desenharTodosDaLista(pagina, carimbo.pagamentos);
                await desenharTodosDaLista(pagina, carimbo.datas);
                await desenharTodosDaLista(pagina, carimbo.textos);
            }

            const bytesFinal = await pdfDocLib.save();
            const arquivoAssinado = new File([bytesFinal], documento.nomeArquivoSaida, { type: "application/pdf" });

            await onConfirmar(arquivoAssinado);
            onClose();
        } catch (error) {
            console.error("Erro ao assinar documento", error);
            toast.error("Erro ao assinar o documento");
        } finally {
            setEnviando(false);
        }
    }

    if (!documento) return null;

    const paginaTemAlgoSalvo = paginaAtual in carimbosPorPagina;
    const nadaPraSalvar = !paginaTemAlgo() && Object.keys(carimbosPorPagina).length === 0;

    return (
        <Dialog
            contentClassName="p-0"
            blockScroll
            maximizable
            visible={!!documento}
            onHide={onClose}
            header={documento.titulo}
            style={{ width: "90%", maxWidth: "1000px" }}
        >
            {carregando && (
                <div className="flex items-center justify-center h-[50vh]">
                    <i className="fa fa-spinner fa-spin text-2xl text-muted-foreground" />
                </div>
            )}

            {!carregando && semAssinaturaCadastrada && (
                <div className="flex flex-col items-center gap-3 py-16 px-6 text-center">
                    <i className="fa fa-signature text-3xl text-muted-foreground" />
                    <p className="text-sm text-muted-foreground max-w-sm">
                        Você ainda não cadastrou uma assinatura. Cadastre uma na aba "Assinatura" em Meu Usuário para poder assinar documentos.
                    </p>
                    <Button
                        variant="blue"
                        label="Ir para Meu Usuário"
                        iconLeft="fa fa-user-circle"
                        onClick={() => { onClose(); window.open("/meu-usuario", "_blank"); }}
                    />
                </div>
            )}

            {!carregando && !semAssinaturaCadastrada && erroFinal && (
                <p className="text-sm text-destructive text-center py-16">{erroFinal}</p>
            )}

            {!carregando && !semAssinaturaCadastrada && !erroFinal && (
                <div className="flex flex-col sm:flex-row" style={{ height: "75vh" }}>
                    {/* Área do documento — rola independente do painel de ações ao lado. */}
                    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                        {numPaginas > 1 && (
                            <div className="flex items-center justify-center gap-3 py-2 border-b border-border bg-card shrink-0">
                                <button
                                    type="button"
                                    disabled={paginaAtual <= 1}
                                    onClick={() => irParaPagina(paginaAtual - 1)}
                                    className="p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                                >
                                    <i className="fa fa-chevron-left" />
                                </button>
                                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                                    Página {paginaAtual} de {numPaginas}
                                    {paginaTemAlgoSalvo && (
                                        <i className="fa fa-check-circle text-success" title="Página com carimbos salvos" />
                                    )}
                                </span>
                                <button
                                    type="button"
                                    disabled={paginaAtual >= numPaginas}
                                    onClick={() => irParaPagina(paginaAtual + 1)}
                                    className="p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                                >
                                    <i className="fa fa-chevron-right" />
                                </button>
                            </div>
                        )}

                        <div className="flex-1 flex items-start justify-center bg-muted/30 p-4 overflow-auto">
                            <div
                                className="relative bg-white shadow shrink-0"
                                style={{ width: tamanhoCanvas.width, height: tamanhoCanvas.height }}
                            >
                                <canvas ref={canvasRef} className="absolute inset-0" />

                                {renderizandoPagina && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                                        <i className="fa fa-spinner fa-spin text-xl text-muted-foreground" />
                                    </div>
                                )}

                                {!renderizandoPagina && assinaturaUrl && assinaturas.map(item => (
                                    <Fragment key={item.id}>
                                        <img
                                            src={assinaturaUrl}
                                            alt="Assinatura"
                                            draggable={false}
                                            onPointerDown={iniciarMover("assinatura", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute select-none"
                                            style={{
                                                left: item.caixa.x,
                                                top: item.caixa.y,
                                                width: item.caixa.width,
                                                height: item.caixa.height,
                                                cursor: "grab",
                                                border: "1px dashed rgba(37, 99, 235, 0.6)",
                                            }}
                                        />
                                        <div
                                            onPointerDown={iniciarRedimensionar("assinatura", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute bg-primary rounded-full border-2 border-white shadow"
                                            style={{
                                                left: item.caixa.x + item.caixa.width - 7,
                                                top: item.caixa.y + item.caixa.height - 7,
                                                width: 14,
                                                height: 14,
                                                cursor: "nwse-resize",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removerAssinatura(item.id)}
                                            title="Remover assinatura"
                                            className="absolute flex items-center justify-center bg-destructive text-destructive-foreground rounded-full shadow"
                                            style={{ left: item.caixa.x - 8, top: item.caixa.y - 8, width: 18, height: 18 }}
                                        >
                                            <i className="fa fa-times" style={{ fontSize: 9 }} />
                                        </button>
                                    </Fragment>
                                ))}

                                {!renderizandoPagina && rateios.map(item => (
                                    <Fragment key={item.id}>
                                        <img
                                            src={item.url}
                                            alt="Tabela de rateio orçamentário"
                                            draggable={false}
                                            onPointerDown={iniciarMover("rateio", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute select-none"
                                            style={{
                                                left: item.caixa.x,
                                                top: item.caixa.y,
                                                width: item.caixa.width,
                                                height: item.caixa.height,
                                                cursor: "grab",
                                                border: "1px dashed rgba(22, 163, 74, 0.7)",
                                            }}
                                        />
                                        <div
                                            onPointerDown={iniciarRedimensionar("rateio", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute bg-success rounded-full border-2 border-white shadow"
                                            style={{
                                                left: item.caixa.x + item.caixa.width - 7,
                                                top: item.caixa.y + item.caixa.height - 7,
                                                width: 14,
                                                height: 14,
                                                cursor: "nwse-resize",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removerRateio(item.id)}
                                            title="Remover rateio"
                                            className="absolute flex items-center justify-center bg-destructive text-destructive-foreground rounded-full shadow"
                                            style={{ left: item.caixa.x - 8, top: item.caixa.y - 8, width: 18, height: 18 }}
                                        >
                                            <i className="fa fa-times" style={{ fontSize: 9 }} />
                                        </button>
                                    </Fragment>
                                ))}

                                {!renderizandoPagina && pagamentos.map(item => (
                                    <Fragment key={item.id}>
                                        <img
                                            src={item.url}
                                            alt="Forma de pagamento"
                                            draggable={false}
                                            onPointerDown={iniciarMover("pagamento", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute select-none"
                                            style={{
                                                left: item.caixa.x,
                                                top: item.caixa.y,
                                                width: item.caixa.width,
                                                height: item.caixa.height,
                                                cursor: "grab",
                                                border: "1px dashed rgba(217, 119, 6, 0.7)",
                                            }}
                                        />
                                        <div
                                            onPointerDown={iniciarRedimensionar("pagamento", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute bg-amber-600 rounded-full border-2 border-white shadow"
                                            style={{
                                                left: item.caixa.x + item.caixa.width - 7,
                                                top: item.caixa.y + item.caixa.height - 7,
                                                width: 14,
                                                height: 14,
                                                cursor: "nwse-resize",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removerPagamento(item.id)}
                                            title="Remover forma de pagamento"
                                            className="absolute flex items-center justify-center bg-destructive text-destructive-foreground rounded-full shadow"
                                            style={{ left: item.caixa.x - 8, top: item.caixa.y - 8, width: 18, height: 18 }}
                                        >
                                            <i className="fa fa-times" style={{ fontSize: 9 }} />
                                        </button>
                                    </Fragment>
                                ))}

                                {!renderizandoPagina && datas.map(item => (
                                    <Fragment key={item.id}>
                                        <img
                                            src={item.url}
                                            alt="Data de lançamento"
                                            draggable={false}
                                            onPointerDown={iniciarMover("data", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute select-none"
                                            style={{
                                                left: item.caixa.x,
                                                top: item.caixa.y,
                                                width: item.caixa.width,
                                                height: item.caixa.height,
                                                cursor: "grab",
                                                border: "1px dashed rgba(147, 51, 234, 0.7)",
                                            }}
                                        />
                                        <div
                                            onPointerDown={iniciarRedimensionar("data", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute rounded-full border-2 border-white shadow"
                                            style={{
                                                left: item.caixa.x + item.caixa.width - 7,
                                                top: item.caixa.y + item.caixa.height - 7,
                                                width: 14,
                                                height: 14,
                                                cursor: "nwse-resize",
                                                backgroundColor: "#9333ea",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removerData(item.id)}
                                            title="Remover data de lançamento"
                                            className="absolute flex items-center justify-center bg-destructive text-destructive-foreground rounded-full shadow"
                                            style={{ left: item.caixa.x - 8, top: item.caixa.y - 8, width: 18, height: 18 }}
                                        >
                                            <i className="fa fa-times" style={{ fontSize: 9 }} />
                                        </button>
                                    </Fragment>
                                ))}

                                {!renderizandoPagina && textos.map(item => (
                                    <Fragment key={item.id}>
                                        <img
                                            src={item.url}
                                            alt="Texto livre"
                                            draggable={false}
                                            onPointerDown={iniciarMover("texto", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute select-none"
                                            style={{
                                                left: item.caixa.x,
                                                top: item.caixa.y,
                                                width: item.caixa.width,
                                                height: item.caixa.height,
                                                cursor: "grab",
                                                border: "1px dashed rgba(71, 85, 105, 0.7)",
                                            }}
                                        />
                                        <div
                                            onPointerDown={iniciarRedimensionar("texto", item.id)}
                                            onPointerMove={moverOuRedimensionar}
                                            onPointerUp={pararInteracao}
                                            className="absolute rounded-full border-2 border-white shadow"
                                            style={{
                                                left: item.caixa.x + item.caixa.width - 7,
                                                top: item.caixa.y + item.caixa.height - 7,
                                                width: 14,
                                                height: 14,
                                                cursor: "nwse-resize",
                                                backgroundColor: "#475569",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removerTexto(item.id)}
                                            title="Remover texto"
                                            className="absolute flex items-center justify-center bg-destructive text-destructive-foreground rounded-full shadow"
                                            style={{ left: item.caixa.x - 8, top: item.caixa.y - 8, width: 18, height: 18 }}
                                        >
                                            <i className="fa fa-times" style={{ fontSize: 9 }} />
                                        </button>
                                    </Fragment>
                                ))}
                            </div>
                        </div>

                        <p className="text-xs text-muted-foreground text-center py-2 border-t border-border bg-card shrink-0">
                            Arraste cada carimbo para posicionar; use a alça no canto para redimensionar e o × para remover.
                            {numPaginas > 1 && " Trocar de página salva automaticamente os carimbos da página atual."}
                        </p>
                    </div>

                    {/* Painel lateral fixo — não rola junto com o documento, fica sempre visível. */}
                    <div className="w-full sm:w-56 shrink-0 border-t sm:border-t-0 sm:border-l border-border bg-card p-3 flex flex-col gap-2">

                        {/* Ações principais */}
                        <div className="flex flex-col gap-2">
                            <Button
                                variant="blue"
                                label="Adicionar Assinatura"
                                iconLeft="fa fa-signature"
                                disabled={renderizandoPagina}
                                onClick={adicionarAssinatura}
                                className="w-full justify-start"
                            />

                            <Button
                                variant="green"
                                label="Adicionar Rateio"
                                iconLeft="fa fa-table"
                                disabled={!temRateioDisponivel || renderizandoPagina}
                                onClick={abrirDialogoRateio}
                                className="w-full justify-start"
                            />

                            <Button
                                variant="white"
                                label="Adicionar Forma Pgto."
                                iconLeft="fa fa-money-check-alt"
                                disabled={renderizandoPagina}
                                onClick={abrirDialogoPagamento}
                                className="w-full justify-start"
                            />

                            <Button
                                variant="white"
                                label="Adicionar Data"
                                iconLeft="fa fa-calendar-day"
                                loading={gerandoImagemData}
                                disabled={renderizandoPagina}
                                onClick={adicionarDataLancamento}
                                className="w-full justify-start"
                            />

                            <Button
                                variant="grayLight"
                                label="Adicionar Texto Livre"
                                iconLeft="fa fa-font"
                                disabled={renderizandoPagina}
                                onClick={abrirDialogoTexto}
                                className="w-full justify-start"
                            />
                        </div>

                        {/* Espaço flexível entre os grupos */}
                        <div className="flex-1" />

                        {/* Ações finais */}
                        <div className="flex flex-col gap-2">
                            <Button
                                variant="blue"
                                label="Salvar e Fechar"
                                iconLeft="fa fa-check"
                                loading={enviando}
                                disabled={renderizandoPagina || nadaPraSalvar}
                                onClick={confirmarDocumento}
                                className="w-full justify-start"
                            />

                            <Button
                                variant="gray"
                                label="Cancelar"
                                iconLeft="fa fa-times"
                                disabled={enviando}
                                onClick={onClose}
                                className="w-full justify-start"
                            />
                        </div>
                    </div>
                </div>
            )}

            <Dialog
                visible={confirmandoRateio}
                onHide={() => setConfirmandoRateio(false)}
                header="Adicionar Rateio Orçamentário"
                style={{ width: "90%", maxWidth: "800px" }}
            >
                <div className="flex flex-col gap-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                        Esta solicitação pode ter rateio de mais de uma nota fiscal. Marque a(s) nota(s) fiscal(is)
                        que estão neste documento — um mesmo PDF pode reunir várias notas. Na próxima tela você
                        poderá arrastar a tabela de rateio para onde quiser na página, igual à assinatura.
                    </p>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">
                            Notas Fiscais deste documento<span className="text-destructive ml-1">*</span>
                        </label>
                        <div className="border border-input rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                            {notasFiscaisDisponiveis.map(nota => {
                                const marcada = notasFiscaisSelecionadas.includes(nota.value);
                                return (
                                    <label
                                        key={nota.value}
                                        className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-muted"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={marcada}
                                            onChange={() => alternarNotaFiscal(nota.value)}
                                            className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/15"
                                        />
                                        {nota.label}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {notasFiscaisSelecionadas.length > 0 && (
                        <RateioOrcamentarioTabela itens={itensRateioDasNotasSelecionadas} />
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <Button variant="gray" label="Voltar" onClick={() => setConfirmandoRateio(false)} disabled={gerandoImagemRateio} />
                        <Button
                            variant="green"
                            label="Continuar e Posicionar"
                            iconLeft="fa fa-arrow-right"
                            loading={gerandoImagemRateio}
                            disabled={notasFiscaisSelecionadas.length === 0}
                            onClick={confirmarNotasRateio}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={confirmandoPagamento}
                onHide={() => setConfirmandoPagamento(false)}
                header="Adicionar Forma de Pagamento"
                style={{ width: "90%", maxWidth: "500px" }}
            >
                {pagamentoForm && (
                    <div className="flex flex-col gap-4 pt-2">
                        <p className="text-sm text-muted-foreground">
                            Informe a forma de pagamento deste documento. Na próxima tela você poderá arrastar o
                            carimbo para onde quiser na página, igual à assinatura e ao rateio.
                        </p>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">
                                Forma de Pagamento<span className="text-destructive ml-1">*</span>
                            </label>
                            <CustomDropdown
                                value={pagamentoForm.tipoPagamento}
                                onChange={e => setPagamentoForm(f => ({ ...f, tipoPagamento: e.value }))}
                                options={tiposPagamento}
                                optionValue="nome"
                                optionLabel="nome"
                                placeholder="Selecione"
                                filter
                            />
                        </div>

                        {pagamentoForm.tipoPagamento === "Transferência" && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2 flex flex-col gap-1.5">
                                    <label className="text-sm font-medium text-foreground/80">Tipo de Conta</label>
                                    <CustomDropdown
                                        value={pagamentoForm.tipoConta}
                                        onChange={e => setPagamentoForm(f => ({ ...f, tipoConta: e.value }))}
                                        options={TIPOS_CONTA_PAGAMENTO}
                                        optionValue="nome"
                                        optionLabel="nome"
                                        placeholder="Selecione"
                                    />
                                </div>
                                <Input label="Agência" value={pagamentoForm.agencia} onChange={e => setPagamentoForm(f => ({ ...f, agencia: e.target.value }))} />
                                <Input label="Dígito" value={pagamentoForm.agenciaDigito} onChange={e => setPagamentoForm(f => ({ ...f, agenciaDigito: e.target.value }))} />
                                <Input label="Conta" value={pagamentoForm.conta} onChange={e => setPagamentoForm(f => ({ ...f, conta: e.target.value }))} />
                                <Input label="Dígito" value={pagamentoForm.contaDigito} onChange={e => setPagamentoForm(f => ({ ...f, contaDigito: e.target.value }))} />
                            </div>
                        )}

                        {pagamentoForm.tipoPagamento === "PIX" && (
                            <Input label="Chave PIX" value={pagamentoForm.chavePix} onChange={e => setPagamentoForm(f => ({ ...f, chavePix: e.target.value }))} />
                        )}

                        <div className="flex items-center justify-end gap-2">
                            <Button variant="gray" label="Voltar" onClick={() => setConfirmandoPagamento(false)} disabled={gerandoImagemPagamento} />
                            <Button
                                variant="green"
                                label="Continuar e Posicionar"
                                iconLeft="fa fa-arrow-right"
                                loading={gerandoImagemPagamento}
                                disabled={!pagamentoForm.tipoPagamento}
                                onClick={confirmarFormaPagamento}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog
                visible={confirmandoTexto}
                onHide={() => setConfirmandoTexto(false)}
                header="Adicionar Texto Livre"
                style={{ width: "90%", maxWidth: "500px" }}
            >
                <div className="flex flex-col gap-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                        Digite o texto que será carimbado no documento. Na próxima tela você poderá arrastar o
                        carimbo para onde quiser na página, igual aos demais.
                    </p>

                    <Textarea
                        label="Texto"
                        required
                        rows={4}
                        value={textoLivre}
                        onChange={e => setTextoLivre(e.target.value)}
                        placeholder="Digite o texto livre..."
                    />

                    <div className="flex items-center justify-end gap-2">
                        <Button variant="gray" label="Voltar" onClick={() => setConfirmandoTexto(false)} disabled={gerandoImagemTexto} />
                        <Button
                            variant="green"
                            label="Continuar e Posicionar"
                            iconLeft="fa fa-arrow-right"
                            loading={gerandoImagemTexto}
                            disabled={!textoLivre.trim()}
                            onClick={confirmarTextoLivre}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={pedindoSenha}
                onHide={() => { setPedindoSenha(false); onClose(); }}
                header="Documento protegido por senha"
                style={{ width: "90%", maxWidth: "420px" }}
            >
                <form
                    className="flex flex-col gap-4 pt-2"
                    onSubmit={e => {
                        e.preventDefault();
                        if (!senhaDigitada) return;
                        setErro(null);
                        setPedindoSenha(false);
                        respostaSenhaRef.current?.(senhaDigitada);
                    }}
                >
                    <p className="text-sm text-muted-foreground">
                        Este PDF exige senha para ser aberto. O arquivo assinado será salvo sem a proteção por senha.
                    </p>
                    <Input
                        label="Senha"
                        type="password"
                        required
                        value={senhaDigitada}
                        onChange={e => setSenhaDigitada(e.target.value)}
                        error={senhaIncorreta ? "Senha incorreta. Tente novamente." : undefined}
                        touched={senhaIncorreta}
                    />
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="gray" label="Cancelar" onClick={() => { setPedindoSenha(false); onClose(); }} />
                        <Button type="submit" variant="blue" label="Abrir" iconLeft="fa fa-unlock" disabled={!senhaDigitada} />
                    </div>
                </form>
            </Dialog>
        </Dialog>
    );
}
